import { useEffect, useRef, useState } from "react";
import api, { formatApiError, eur, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, MapPin, Trash2, Building2, Users, Receipt, Target, RefreshCw, Database, Activity, Shuffle, Lock, ChevronDown, Clock } from "lucide-react";
import ProspectAssignmentPanel from "@/components/ProspectAssignmentPanel";

function SyncLogList({ rows, testid, showEmpty = false, title = "Storico sincronizzazioni" }) {
  if ((!rows || rows.length === 0) && !showEmpty) return null;
  return (
    <div className="mt-3 border-t border-border/60 pt-3" data-testid={testid}>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-muted-foreground" data-testid={`${testid}-empty`}>Nessuna sincronizzazione ancora registrata.</div>
      ) : (
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-2 text-xs" data-testid={`${testid}-row`}>
            <span className="text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleString("it-IT") : "—"}{r.mode ? ` · ${r.mode}` : ""}</span>
            <span className="flex items-center gap-2">
              {r.kind === "vendite"
                ? <span className="text-muted-foreground">ordini {r.orders ?? 0} · nuovi {r.created ?? 0} · promossi {r.promoted ?? 0}</span>
                : r.kind === "preventivi"
                ? <span className="text-muted-foreground">preventivi {r.quotes ?? 0}</span>
                : r.kind === "anagrafiche_full"
                ? <span className="text-muted-foreground">aggiornate {r.updated ?? 0} · clienti {r.created_clienti ?? 0} · prospect {r.created_prospect ?? 0}</span>
                : r.kind === "partner"
                ? <span className="text-muted-foreground">partner {r.partner_totali ?? 0} · flaggati {r.flagged ?? 0} · promossi {r.promoted ?? 0} · rimossi {r.unflagged ?? 0}</span>
                : r.kind === "agenti"
                ? <span className="text-muted-foreground">agenti {r.total ?? 0} · attivi {r.attivi ?? 0} · nuovi {r.added ?? 0}</span>
                : r.kind === "attivita"
                ? <span className="text-muted-foreground">nuove {r.created ?? 0} · allineate {r.adopted ?? 0} · senza cliente {r.unmatched ?? 0}</span>
                : <span className="text-muted-foreground">abbinate {r.matched ?? 0} · aggiornate {r.updated ?? 0}</span>}
              {r.outcome === "error"
                ? <Badge className="bg-destructive text-white">errore</Badge>
                : <Badge className="bg-emerald-600 text-white">ok</Badge>}
            </span>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

function SyncSemaphore({ rows, running, staleHours = 30 }) {
  const last = rows && rows.length ? rows[0] : null;
  let status = "green", label = "Allineato", detail = "";
  if (running) {
    status = "blue"; label = "Sincronizzazione in corso";
  } else if (!last) {
    status = "amber"; label = "Mai sincronizzato"; detail = "nessuna sincronizzazione registrata";
  } else {
    const ageH = last.created_at ? (Date.now() - new Date(last.created_at).getTime()) / 36e5 : 999;
    const tot = last.partner_totali ?? 0;
    const when = last.created_at ? new Date(last.created_at).toLocaleString("it-IT") : "—";
    if (last.outcome === "error") {
      status = "red"; label = "Ultima sincronizzazione in ERRORE"; detail = last.message || `del ${when}`;
    } else if (ageH > staleHours) {
      status = "red"; label = "Sincronizzazione notturna MANCANTE"; detail = `ultima ${when}`;
    } else if (tot < 10) {
      status = "amber"; label = "Lista partner troppo corta"; detail = `solo ${tot} in elenco (allineamento non applicato per sicurezza)`;
    } else {
      status = "green"; label = "Allineato al gestionale"; detail = `${when} · ${tot} in elenco · ${last.unflagged ?? 0} declassati`;
    }
  }
  const dot = { green: "bg-emerald-500", red: "bg-red-500", amber: "bg-amber-500", blue: "bg-blue-500" }[status];
  const ring = { green: "border-emerald-500/40 bg-emerald-500/10", red: "border-red-500/50 bg-red-500/10", amber: "border-amber-500/40 bg-amber-500/10", blue: "border-blue-500/40 bg-blue-500/10" }[status];
  const txt = { green: "text-emerald-300", red: "text-red-300", amber: "text-amber-300", blue: "text-blue-300" }[status];
  return (
    <div className={`mt-4 flex items-center gap-3 rounded-md border px-3 py-2 ${ring}`} data-testid="fier-partner-semaphore" data-status={status}>
      <span className="relative flex h-3 w-3 shrink-0">
        {(status === "red" || status === "blue") && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${dot}`} />}
        <span className={`relative inline-flex h-3 w-3 rounded-full ${dot}`} />
      </span>
      <div className="min-w-0">
        <div className={`text-xs font-bold ${txt}`} data-testid="fier-partner-semaphore-label">{label}</div>
        {detail && <div className="truncate text-[11px] text-muted-foreground" data-testid="fier-partner-semaphore-detail">{detail}</div>}
      </div>
    </div>
  );
}

function SyncToggleBar({ id, enabled, onToggle, title, icon: Icon, badge = false }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3" data-testid={`sync-bar-${id}`}>
      {!enabled ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {Icon && <Icon className="h-5 w-5 shrink-0 text-primary" />}
          <span className="font-heading text-sm font-bold">{title}</span>
          {badge && <span className="rounded bg-primary/70 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground">Fier</span>}
          <Badge className="bg-white/10 text-muted-foreground" data-testid={`sync-state-${id}`}>Disattivato · cron notturno fermo</Badge>
        </div>
      ) : (
        <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-500" data-testid={`sync-state-${id}`}>● Attivo</span>
      )}
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-[11px] text-muted-foreground">{enabled ? "On" : "Off"}</span>
        <Switch checked={enabled} onCheckedChange={(v) => onToggle(id, v)} data-testid={`sync-toggle-${id}`} />
      </div>
    </div>
  );
}

const KINDS = [
  { kind: "anagrafiche", title: "★ Anagrafiche gestionale", desc: "Arricchisce Clienti/Partner (Attiva), crea Prospect (Potenziale) e li assegna alle agenzie via agente", icon: Database },
  { kind: "vendite", title: "3 · Vendite", desc: "Storico ordini Torri (kWp, importi, margine)", icon: Receipt },
  { kind: "prospects", title: "Inserimento prospect", desc: "Aziende da acquisire — assegnate via colonna 'Agenzia' del file, altrimenti per territorio (provincia/regione), altrimenti direzionali", icon: Target },
  { kind: "attivita", title: "5 · Attività (altri CRM)", desc: "Azioni svolte dalla rete (visite, telefonate, messaggi) — abbinate ai clienti e alle agenzie automaticamente", icon: Activity },
];

function ImportCard({ kind, title, desc, icon: Icon, onDone, agencies = [] }) {
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agencyId, setAgencyId] = useState("none");
  const isProspect = kind === "prospects";

  const doPreview = async (f) => {
    if (!f) return;
    setBusy(true); setPreview(null);
    const fd = new FormData();
    fd.append("file", f); fd.append("kind", kind);
    if (isProspect && agencyId !== "none") fd.append("agency_id", agencyId);
    try {
      const r = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    if (isProspect && agencyId !== "none") fd.append("agency_id", agencyId);
    try {
      const r = await api.post("/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.message);
      setFile(null); setPreview(null);
      if (inputRef.current) inputRef.current.value = "";
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid={`import-card-${kind}`}>
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <div>
          <h3 className="font-heading text-sm font-bold">{title}</h3>
          <p className="text-xs text-muted-foreground">{desc}</p>
        </div>
      </div>

      {isProspect && (
        <div className="mb-3">
          <Select value={agencyId} onValueChange={setAgencyId}>
            <SelectTrigger className="h-9" data-testid="prospect-agency-select"><SelectValue placeholder="Assegnazione automatica…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Automatica (colonna 'Agenzia' del file → territorio → direzionali)</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>Forza tutti su: {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
        data-testid={`import-file-${kind}`}
        onChange={(e) => { const f = e.target.files?.[0]; setFile(f); doPreview(f); }} />
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`import-choose-${kind}`}>
          <FileSpreadsheet className="mr-2 h-4 w-4" /> {file ? file.name.slice(0, 28) : "Scegli file Excel"}
        </Button>
        {busy && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
      </div>

      {preview && (
        <div className="mt-4 space-y-3" data-testid={`import-preview-${kind}`}>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">Righe: <b className="ml-1">{preview.total}</b></Badge>
            {Object.entries(preview.stats || {}).map(([k, v]) => (
              <Badge key={k} variant="secondary" className="text-[11px]">{k}: <b className="ml-1">{v}</b></Badge>
            ))}
          </div>
          {preview.reconcile && (
            <div className="flex flex-wrap gap-2" data-testid={`reconcile-${kind}`}>
              {kind === "prospects" ? (
                <>
                  <Badge className="bg-emerald-600 text-white">Nuovi: {preview.reconcile.nuovi}</Badge>
                  <Badge variant="secondary">Già presenti: {preview.reconcile.gia_presenti}</Badge>
                </>
              ) : kind === "attivita" ? (
                <>
                  <Badge className="bg-emerald-600 text-white">Nuove: {preview.reconcile.nuove}</Badge>
                  <Badge className="bg-blue-600 text-white">Abbinate: {preview.reconcile.abbinate}</Badge>
                  <Badge variant="secondary">Già presenti: {preview.reconcile.gia_presenti}</Badge>
                </>
              ) : kind === "anagrafiche" ? (
                <>
                  <Badge className="bg-blue-600 text-white">Partner arricchiti: {preview.reconcile.partner_arricchiti}</Badge>
                  <Badge className="bg-emerald-600 text-white">Partner nuovi: {preview.reconcile.partner_nuovi}</Badge>
                  <Badge className="bg-amber-600 text-white">Prospect: {preview.reconcile.prospect}</Badge>
                </>
              ) : kind !== "vendite" ? (
                <>
                  <Badge className="bg-emerald-600 text-white">Nuovi: {preview.reconcile.nuovi}</Badge>
                  <Badge className="bg-blue-600 text-white">Aggiornati: {preview.reconcile.aggiornati}</Badge>
                  <Badge variant="secondary">Invariati: {preview.reconcile.invariati}</Badge>
                </>
              ) : (
                <>
                  <Badge className="bg-emerald-600 text-white">Nuovi ordini: {preview.reconcile.nuovi_ordini}</Badge>
                  <Badge variant="secondary">Già presenti: {preview.reconcile.gia_presenti}</Badge>
                  <Badge className="bg-amber-600 text-white">Nuovi clienti: {preview.reconcile.nuovi_clienti}</Badge>
                </>
              )}
            </div>
          )}
          {preview.warnings?.length > 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-300">
              <div className="mb-1 flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Avvisi</div>
              <ul className="list-disc pl-4">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <div className="max-h-56 overflow-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted"><tr>{(preview.columns || []).map((c) => <th key={c} className="px-2 py-1.5 text-left font-semibold">{c}</th>)}</tr></thead>
              <tbody>
                {(preview.sample || []).map((row, i) => (
                  <tr key={i} className="border-t border-border">{(preview.columns || []).map((c) => <td key={c} className="whitespace-nowrap px-2 py-1 text-muted-foreground">{String(row[c] ?? "")}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button size="sm" onClick={doImport} disabled={busy} data-testid={`import-commit-${kind}`}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Importa {preview.total} righe
          </Button>
        </div>
      )}
    </Card>
  );
}

function EnrichCard({ enabled = true, onToggle }) {
  const CATS = { partner: "Partner", cliente: "Clienti", agenti: "Agenti" };
  const [cat, setCat] = useState("partner");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [createNew, setCreateNew] = useState(true);
  const [nlCat, setNlCat] = useState("");
  const [includeSupp, setIncludeSupp] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setFile(f); setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("category", cat); fd.append("file", f); fd.append("include_suppressed", includeSupp);
      const r = await api.post("/enrich/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Verifica fallita"); setFile(null); }
    finally { setBusy(false); }
  };

  const toggleSupp = async (checked) => {
    setIncludeSupp(checked);
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("category", cat); fd.append("file", file); fd.append("include_suppressed", checked);
      const r = await api.post("/enrich/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e2) { toast.error("Verifica fallita"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("category", cat); fd.append("file", file); fd.append("create_new", createNew); fd.append("newsletter_category", nlCat); fd.append("include_suppressed", includeSupp);
      const r = await api.post("/enrich/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = r.data.stats;
      toast.success(`Arricchite ${s.enriched} schede${r.data.created_new ? ` · create ${s.new} nuove` : ""}${r.data.newsletter_added ? ` · ${r.data.newsletter_added} email in Rubrica` : ""}${s.bounced_restored ? ` · ${s.bounced_restored} bounce/disiscritti ripristinati` : ""}${s.bounced_skipped ? ` · ${s.bounced_skipped} bounce/disiscritti esclusi${s.bounced_in_crm ? ` (${s.bounced_in_crm} già a CRM ora soppressi)` : ""}` : ""} · invariate ${s.unchanged}`);
      setPreview(null); setFile(null);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Applicazione fallita"); }
    finally { setBusy(false); }
  };

  const s = preview?.stats;
  return (
    <Card className="border-primary/30 bg-primary/5 p-5" data-testid="enrich-card">
      <SyncToggleBar id="enrich" enabled={enabled} onToggle={onToggle} title="Arricchisci informazioni" icon={Database} />
      {enabled && (<>
      <div className="flex items-center gap-2">
        <Database className="h-5 w-5 text-primary" />
        <h3 className="font-heading text-sm font-bold">Arricchisci informazioni</h3>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Carica un CSV/Excel: il software <b>verifica</b> e aggiunge alle schede esistenti (Agenti, Partner, Clienti) solo le informazioni <b>mancanti</b> (email, telefono, agente, P.IVA, indirizzo…). Abbinamento per P.IVA/email o, in mancanza, per nome.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select value={cat} onValueChange={(v) => { setCat(v); setPreview(null); setFile(null); }}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="enrich-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(CATS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`enrich-cat-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="enrich-file-input" />
        <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="enrich-choose-btn">
          {busy && !preview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Carica CSV/Excel
        </Button>
      </div>

      {preview && s && (
        <div className="mt-4 space-y-3" data-testid="enrich-preview">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-2"><div className="text-lg font-bold text-emerald-400">{s.enriched}</div><div className="text-[11px] text-muted-foreground">da arricchire</div></div>
            <div className="rounded-md border border-sky-500/30 bg-sky-500/10 p-2"><div className="text-lg font-bold text-sky-400">{s.new}</div><div className="text-[11px] text-muted-foreground">nuove (non trovate)</div></div>
            <div className="rounded-md border border-white/10 bg-white/5 p-2"><div className="text-lg font-bold text-muted-foreground">{s.unchanged}</div><div className="text-[11px] text-muted-foreground">invariate</div></div>
          </div>
          <p className="text-xs text-muted-foreground">Righe totali nel file: <b>{s.total_rows}</b>{s.skipped ? ` · ${s.skipped} senza nome (ignorate)` : ""}{s.bounced_skipped ? ` · ${s.bounced_skipped} bounce/disiscritti (email esclusa)` : ""}{s.bounced_restored ? ` · ${s.bounced_restored} bounce/disiscritti ripristinati` : ""}</p>
          {(s.bounced_skipped > 0 || s.bounced_restored > 0) && (
            <label className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs" data-testid="enrich-include-suppressed">
              <input type="checkbox" checked={includeSupp} onChange={(e) => toggleSupp(e.target.checked)} className="mt-0.5" data-testid="enrich-include-suppressed-checkbox" />
              <span><b>Includi e ripristina i bounce/disiscritti</b> — importa comunque queste email e le riattiva (rimuove dai "Cancellati / Disiscritti"). Usa solo se sei sicuro che siano indirizzi validi.</span>
            </label>
          )}
          {preview.columns && (
            <p className="text-[11px] text-muted-foreground">Colonna nome rilevata: <b className="text-foreground">{preview.name_col || "—"}</b> · Colonne file: {preview.columns.join(", ")}</p>
          )}
          {preview.samples?.enriched?.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-md border border-white/10 p-2 text-xs" data-testid="enrich-sample-list">
              {preview.samples.enriched.map((x, i) => (
                <div key={i} className="flex justify-between gap-2 border-b border-white/5 py-1 last:border-0">
                  <span className="truncate">{x.name}</span>
                  <span className="shrink-0 text-emerald-400">+ {x.fields.join(", ")}</span>
                </div>
              ))}
            </div>
          )}
          {s.new > 0 && (
            <label className="flex items-center gap-2 text-sm" data-testid="enrich-create-new">
              <input type="checkbox" checked={createNew} onChange={(e) => setCreateNew(e.target.checked)} />
              Crea come nuove schede le {s.new} righe non trovate
            </label>
          )}
          <div className="rounded-md border border-sky-500/30 bg-sky-500/10 p-2 text-xs" data-testid="enrich-nl-box">
            <div className="mb-1"><b>{Math.max(0, (preview.emails_in_file || 0) - (s.bounced_skipped || 0))}</b> email valide nel file{s.bounced_skipped ? ` (${s.bounced_skipped} bounce/disiscritti esclusi)` : ""}. Salvale nella Rubrica Newsletter sotto una categoria:</div>
            <input value={nlCat} onChange={(e) => setNlCat(e.target.value)} placeholder="Nome categoria (es. Iscritti 2025) — se non esiste viene creata" className="w-full rounded border border-input bg-background px-2 py-1" data-testid="enrich-nl-category" />
            <div className="mt-1 text-[11px] text-muted-foreground">Lascia vuoto per non aggiungerle alla Rubrica.</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={apply} disabled={busy || (s.enriched === 0 && !(createNew && s.new > 0) && !(nlCat && preview.emails_in_file > 0))} data-testid="enrich-apply-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Applica
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setPreview(null); setFile(null); }} disabled={busy}>Annulla</Button>
          </div>
        </div>
      )}
      </>)}
    </Card>
  );
}

function ReclassifyCard({ enabled = true, onToggle }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const runDry = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/reclassify-anagrafiche?confirm=false"); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runConfirm = async () => {
    if (!window.confirm(`Confermi la ri-classificazione? ${preview.spostati_a_prospect} anagrafiche senza acquisti diventeranno Prospect e il flag Partner verrà azzerato. Verrà creato un backup automatico. Operazione applicata al database.`)) return;
    setBusy(true);
    try { const r = await api.post("/admin/reclassify-anagrafiche?confirm=true"); toast.success(r.data.message); setPreview({ ...r.data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-primary/30 bg-primary/5 p-5" data-testid="reclassify-card">
      <SyncToggleBar id="reclassify" enabled={enabled} onToggle={onToggle} title="Ri-classifica anagrafiche (Cliente / Prospect / Partner)" icon={Shuffle} />
      {enabled && (<>
      <div className="mb-2 flex items-center gap-2">
        <Shuffle className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-sm font-bold">Ri-classifica anagrafiche (Cliente / Prospect / Partner)</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Allinea il database alle definizioni: <b>Cliente</b> = almeno un acquisto (ordine Torri o n° ordini gestionale &gt; 0),
        <b> Prospect</b> = nessun acquisto, <b>Partner</b> = Cliente con partnership firmata (flag manuale). Le anagrafiche senza acquisti
        vengono spostate tra i Prospect e il flag Partner viene azzerato. Viene creato un backup automatico prima di applicare.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={runDry} disabled={busy} data-testid="reclassify-preview-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Activity className="mr-2 h-4 w-4" />} Anteprima
        </Button>
        {preview && preview.dry_run && (
          <Button size="sm" onClick={runConfirm} disabled={busy} data-testid="reclassify-confirm-btn">
            Applica ri-classificazione
          </Button>
        )}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" />Eseguita automaticamente <b className="mx-1">ogni notte alle 05:00</b> (backup incluso).</div>
      {preview && (
        <div className="mt-3 rounded-md border border-border bg-background p-3 text-xs" data-testid="reclassify-summary">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
            <div><span className="text-muted-foreground">Anagrafiche totali</span><div className="font-heading text-base font-bold">{num(preview.total_partners)}</div></div>
            <div><span className="text-muted-foreground">Restano Clienti</span><div className="font-heading text-base font-bold text-emerald-600">{num(preview.restano_clienti)}</div></div>
            <div><span className="text-muted-foreground">→ Prospect</span><div className="font-heading text-base font-bold text-amber-600">{num(preview.spostati_a_prospect)}</div></div>
            <div><span className="text-muted-foreground">Flag Partner azzerati</span><div className="font-heading text-base font-bold">{num(preview.flag_partner_resettati)}</div></div>
          </div>
          <p className={`mt-2 ${preview.dry_run ? "text-muted-foreground" : "font-semibold text-emerald-600"}`}>{preview.message}</p>
        </div>
      )}
      </>)}
    </Card>
  );
}

function ReassociateCard({ enabled = true, onToggle }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const runDry = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/reassociate-activities?confirm=false"); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const runConfirm = async () => {
    if (!window.confirm(`Confermi la ri-associazione? ${preview.updated} attività verranno ri-collegate a Clienti/Prospect in base allo stato attuale. Verrà creato un backup automatico.`)) return;
    setBusy(true);
    try { const r = await api.post("/admin/reassociate-activities?confirm=true"); toast.success(r.data.message); setPreview({ ...r.data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-primary/30 bg-primary/5 p-5" data-testid="reassociate-card">
      <SyncToggleBar id="reassociate" enabled={enabled} onToggle={onToggle} title="Ri-associa attività a Clienti / Prospect" icon={RefreshCw} />
      {enabled && (<>
      <div className="mb-2 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-sm font-bold">Ri-associa attività a Clienti / Prospect</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Ricollega le attività (visite, telefonate, messaggi) al Cliente o Prospect corretto in base allo stato attuale del database,
        così gli <b>indicatori di rete</b> (agenzie e responsabile) non si sballano dopo import o ri-classificazioni. Le attività di
        agenzia senza riferimento a un cliente non vengono toccate. Backup automatico prima di applicare.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={runDry} disabled={busy} data-testid="reassociate-preview-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Activity className="mr-2 h-4 w-4" />} Anteprima
        </Button>
        {preview && preview.dry_run && (
          <Button size="sm" onClick={runConfirm} disabled={busy} data-testid="reassociate-confirm-btn">
            Applica ri-associazione
          </Button>
        )}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" />Eseguita automaticamente <b className="mx-1">ogni notte alle 05:00</b> insieme alla ri-classificazione.</div>
      {preview && (
        <div className="mt-3 rounded-md border border-border bg-background p-3 text-xs" data-testid="reassociate-summary">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
            <div><span className="text-muted-foreground">Da ri-associare</span><div className="font-heading text-base font-bold">{num(preview.updated)}</div></div>
            <div><span className="text-muted-foreground">Su clienti/partner</span><div className="font-heading text-base font-bold text-emerald-600">{num(preview.cliente_partner)}</div></div>
            <div><span className="text-muted-foreground">Su prospect</span><div className="font-heading text-base font-bold text-amber-600">{num(preview.prospect)}</div></div>
            <div><span className="text-muted-foreground">Direzionali</span><div className="font-heading text-base font-bold">{num(preview.direzionale)}</div></div>
          </div>
          <p className={`mt-2 ${preview.dry_run ? "text-muted-foreground" : "font-semibold text-emerald-600"}`}>{preview.message}</p>
        </div>
      )}
      </>)}
    </Card>
  );
}

export default function ImportData() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const vendite = KINDS.find((k) => k.kind === "vendite");
  const others = KINDS.filter((k) => k.kind !== "vendite");
  const [advOpen, setAdvOpen] = useState(false);
  const [geo, setGeo] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [fier, setFier] = useState(null);
  const [fierBusy, setFierBusy] = useState(false);
  const fierPoll = useRef(null);
  const [fierV, setFierV] = useState(null);
  const [fierVBusy, setFierVBusy] = useState(false);
  const [fierQBusy, setFierQBusy] = useState(false);
  const [cutoffOrders, setCutoffOrders] = useState("");
  const [cutoffQuotes, setCutoffQuotes] = useState("");
  const [cutoffBusy, setCutoffBusy] = useState(false);
  const fierVPoll = useRef(null);
  const [fierLog, setFierLog] = useState([]);
  const [fierVLog, setFierVLog] = useState([]);
  const [fierQ, setFierQ] = useState(null);
  const [fierQLog, setFierQLog] = useState([]);
  const [fierFull, setFierFull] = useState(null);
  const [fierFullBusy, setFierFullBusy] = useState(false);
  const fierFullPoll = useRef(null);
  const [fierFullLog, setFierFullLog] = useState([]);
  const [fierDetail, setFierDetail] = useState(null);
  const [fierDetailBusy, setFierDetailBusy] = useState(false);
  const fierDetailPoll = useRef(null);
  const [fierDetailLog, setFierDetailLog] = useState([]);
  const [fierPartner, setFierPartner] = useState(null);
  const [fierPartnerBusy, setFierPartnerBusy] = useState(false);
  const fierPartnerPoll = useRef(null);
  const [fierPartnerLog, setFierPartnerLog] = useState([]);
  const [partnerRestoreBusy, setPartnerRestoreBusy] = useState(false);
  const partnerRestoreRef = useRef(null);
  const [realignBusy, setRealignBusy] = useState(false);
  const [realignMsg, setRealignMsg] = useState("");
  const [fierAgenti, setFierAgenti] = useState(null);
  const [fierAgentiBusy, setFierAgentiBusy] = useState(false);
  const fierAgentiPoll = useRef(null);
  const [fierAgentiLog, setFierAgentiLog] = useState([]);
  const [agentiOrphans, setAgentiOrphans] = useState([]);
  const [fierAct, setFierAct] = useState(null);
  const [fierActBusy, setFierActBusy] = useState(false);
  const fierActPoll = useRef(null);
  const [fierActLog, setFierActLog] = useState([]);
  const [history, setHistory] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [intraday, setIntraday] = useState(null);
  const [intradayBusy, setIntradayBusy] = useState(false);
  const [syncSettings, setSyncSettings] = useState({});
  const isSyncEnabled = (k) => syncSettings[k] !== false;
  const loadSyncSettings = () => api.get("/sync-settings").then((r) => setSyncSettings(r.data || {})).catch(() => {});
  const toggleSync = async (key, enabled) => {
    setSyncSettings((s) => ({ ...s, [key]: enabled }));
    try {
      await api.post("/sync-settings", { key, enabled });
      toast.success(enabled ? "Sincronizzazione riattivata" : "Sincronizzazione disattivata — cron notturno fermo");
    } catch (e) {
      setSyncSettings((s) => ({ ...s, [key]: !enabled }));
      toast.error("Impossibile salvare l'impostazione");
    }
  };

  const loadGeo = () => api.get("/import/geocode-status").then((r) => setGeo(r.data)).catch(() => {});
  const loadHistory = () => api.get("/import/history").then((r) => setHistory(r.data)).catch(() => {});
  const loadIntraday = () => api.get("/dashboard/import-info").then((r) => setIntraday(r.data?.intraday || null)).catch(() => {});
  const runIntraday = async () => {
    setIntradayBusy(true);
    try {
      const r = await api.post("/admin/intraday-sync");
      toast.success(r.data?.message || "Aggiornamento del giorno avviato");
      setTimeout(loadIntraday, 8000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Avvio fallito"); }
    finally { setIntradayBusy(false); }
  };
  useEffect(() => { loadSyncSettings(); loadGeo(); loadHistory(); loadIntraday(); api.get("/agencies").then((r) => setAgencies(r.data || [])).catch(() => {}); const t = setInterval(loadGeo, 3000); return () => clearInterval(t); }, []);
  const onImported = () => { loadGeo(); loadHistory(); };

  const doReset = async () => {
    if (!window.confirm("Azzerare TUTTI i dati (agenzie, partner, ordini, prospect, attività)? Operazione irreversibile. Usala una sola volta prima del primo import reale.")) return;
    setResetting(true);
    try { const r = await api.post("/import/reset"); toast.success(r.data.message); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  const doSync = async () => {
    setSyncing(true);
    try { const r = await api.post("/import/sync"); toast.success(r.data.message); loadGeo(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sincronizzazione fallita"); }
    finally { setSyncing(false); }
  };

  const loadFierLog = () => api.get("/import/fier-sync-log?kind=anagrafiche").then((r) => setFierLog(r.data)).catch(() => {});
  const loadFier = () => api.get("/import/fier-sync-status").then((r) => {
    setFier(r.data);
    if (!r.data.running && fierPoll.current) { clearInterval(fierPoll.current); fierPoll.current = null; loadGeo(); loadFierLog(); }
  }).catch(() => {});
  useEffect(() => { if (isAdmin) { loadFier(); loadFierLog(); } return () => { if (fierPoll.current) clearInterval(fierPoll.current); }; }, [isAdmin]);
  const startFierSync = async () => {
    if (!window.confirm("Avviare la sincronizzazione delle anagrafiche già presenti dal Gestionale Fier? Verranno aggiornate/arricchite le anagrafiche esistenti (indirizzo, P.IVA, agente, telefono, e-mail), ri-geolocalizzate e arricchite con dati camerali, data attivazione, logo e contratto PDF (incrementale). NON verranno create nuove anagrafiche (per quello usa «Acquisisci tutte»).")) return;
    setFierBusy(true);
    try {
      await api.post("/import/fier-sync-start");
      toast.success("Sincronizzazione avviata");
      await loadFier();
      if (fierPoll.current) clearInterval(fierPoll.current);
      fierPoll.current = setInterval(loadFier, 3000);
      if (fierDetailPoll.current) clearInterval(fierDetailPoll.current);
      fierDetailPoll.current = setInterval(loadFierDetail, 5000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la sincronizzazione"); }
    finally { setFierBusy(false); }
  };

  const loadFierVLog = () => api.get("/import/fier-sync-log?kind=vendite").then((r) => setFierVLog(r.data)).catch(() => {});
  const loadFierQLog = () => api.get("/import/fier-sync-log?kind=preventivi").then((r) => setFierQLog(r.data)).catch(() => {});
  const loadFierV = () => Promise.all([
    api.get("/import/fier-vendite-status"),
    api.get("/import/fier-preventivi-status"),
  ]).then(([rv, rq]) => {
    setFierV(rv.data);
    setFierQ(rq.data);
    if (!rv.data.running && !rq.data.running && fierVPoll.current) { clearInterval(fierVPoll.current); fierVPoll.current = null; loadGeo(); loadHistory(); loadFierVLog(); loadFierQLog(); }
  }).catch(() => {});
  useEffect(() => { if (isAdmin) { loadFierV(); loadFierVLog(); loadFierQLog(); } return () => { if (fierVPoll.current) clearInterval(fierVPoll.current); }; }, [isAdmin]);
  useEffect(() => { if (isAdmin) api.get("/import/fier-cutoff").then((r) => { setCutoffOrders(r.data.orders || ""); setCutoffQuotes(r.data.quotes || ""); }).catch(() => {}); }, [isAdmin]);
  const startFierVendite = async (full) => {
    const msg = full
      ? "Avviare la sincronizzazione COMPLETA degli ordini dal Gestionale Fier (tutti gli ordini, ~2.770)? È lenta perché apre ogni scheda ordine per kWp e dettaglio prodotti."
      : "Avviare la sincronizzazione degli ordini dal Gestionale Fier (ultimi 90 giorni)?";
    if (!window.confirm(msg)) return;
    setFierVBusy(true);
    try {
      await api.post("/import/fier-vendite-start", { full });
      toast.success("Sincronizzazione ordini avviata");
      await loadFierV();
      if (fierVPoll.current) clearInterval(fierVPoll.current);
      fierVPoll.current = setInterval(loadFierV, 3000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la sincronizzazione"); }
    finally { setFierVBusy(false); }
  };

  const saveCutoff = async () => {
    if (!window.confirm("Applicare il cut iniziale? I documenti precedenti al mese indicato verranno RIMOSSI dal CRM e non più importati.")) return;
    setCutoffBusy(true);
    try {
      const r = await api.post("/import/fier-cutoff", { orders: cutoffOrders || null, quotes: cutoffQuotes || null });
      toast.success(`Cut applicato · rimossi ${r.data.removed_orders} ordini e ${r.data.removed_quotes} preventivi precedenti`);
      if (typeof loadHistory === "function") loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile applicare il cut"); }
    finally { setCutoffBusy(false); }
  };

  const startFierPreventivi = async (full) => {
    const msg = full
      ? "Avviare la sincronizzazione COMPLETA dei preventivi dal Gestionale Fier? È lenta perché apre ogni scheda preventivo per kWp e dettaglio prodotti."
      : "Avviare la sincronizzazione dei preventivi dal Gestionale Fier (ultimi 90 giorni)?";
    if (!window.confirm(msg)) return;
    setFierQBusy(true);
    try {
      await api.post("/import/fier-preventivi-start", { full });
      toast.success("Sincronizzazione preventivi avviata");
      await loadFierV();
      if (fierVPoll.current) clearInterval(fierVPoll.current);
      fierVPoll.current = setInterval(loadFierV, 3000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la sincronizzazione"); }
    finally { setFierQBusy(false); }
  };

  const loadFierFullLog = () => api.get("/import/fier-sync-log?kind=anagrafiche_full").then((r) => setFierFullLog(r.data)).catch(() => {});

  const loadFierPartnerLog = () => api.get("/import/fier-sync-log?kind=partner").then((r) => setFierPartnerLog(r.data)).catch(() => {});
  const loadFierAgentiLog = () => api.get("/import/fier-sync-log?kind=agenti").then((r) => setFierAgentiLog(r.data)).catch(() => {});
  const loadAgentiOrphans = () => api.get("/import/agenti-orphans").then((r) => setAgentiOrphans(r.data || [])).catch(() => {});
  const loadFierAgenti = () => api.get("/import/fier-agenti-sync-status").then((r) => {
    setFierAgenti(r.data);
    if (!r.data.running && fierAgentiPoll.current) { clearInterval(fierAgentiPoll.current); fierAgentiPoll.current = null; loadFierAgentiLog(); loadAgentiOrphans(); }
  }).catch(() => {});
  useEffect(() => { if (isAdmin) { loadFierAgenti(); loadFierAgentiLog(); loadAgentiOrphans(); } return () => { if (fierAgentiPoll.current) clearInterval(fierAgentiPoll.current); }; }, [isAdmin]);
  const startFierAgenti = async () => {
    if (!window.confirm("Avviare l'importazione degli Agenti dal Gestionale Fier? Verranno AGGIUNTE solo le nuove agenzie/agenti con monitoraggio attivo. Nessun agente esistente verrà cancellato o disattivato.")) return;
    setFierAgentiBusy(true);
    try {
      await api.post("/import/fier-agenti-sync-start");
      toast.success("Importazione agenti avviata");
      await loadFierAgenti();
      if (fierAgentiPoll.current) clearInterval(fierAgentiPoll.current);
      fierAgentiPoll.current = setInterval(loadFierAgenti, 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la sincronizzazione"); }
    finally { setFierAgentiBusy(false); }
  };
  const loadFierActLog = () => api.get("/import/fier-sync-log?kind=attivita").then((r) => setFierActLog(r.data)).catch(() => {});
  const loadFierAct = () => api.get("/import/fier-attivita-sync-status").then((r) => {
    setFierAct(r.data);
    if (!r.data.running && fierActPoll.current) { clearInterval(fierActPoll.current); fierActPoll.current = null; loadFierActLog(); onImported(); }
  }).catch(() => {});
  useEffect(() => { if (isAdmin) { loadFierAct(); loadFierActLog(); } return () => { if (fierActPoll.current) clearInterval(fierActPoll.current); }; }, [isAdmin]);
  const startFierActivities = async () => {
    if (!window.confirm("Sincronizzare le ATTIVITÀ dal Gestionale Fier? Verranno abbinate al cliente e impostate come 'svolte'. I duplicati vengono evitati (le attività già presenti vengono allineate).")) return;
    setFierActBusy(true);
    try {
      await api.post("/import/fier-attivita-sync-start");
      toast.success("Sincronizzazione attività avviata");
      await loadFierAct();
      if (fierActPoll.current) clearInterval(fierActPoll.current);
      fierActPoll.current = setInterval(loadFierAct, 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la sincronizzazione"); }
    finally { setFierActBusy(false); }
  };
  const deleteOrphanAgent = async (o) => {    if (!window.confirm(`Eliminare l'agente "${o.name}"? I suoi ${o.contacts} contatti verranno ceduti per TERRITORIO e, in mancanza, a DIREZIONALE. Operazione non annullabile.`)) return;
    try {
      const r = await api.delete(`/agencies/${o.id}`);
      toast.success(r.data.detail || "Agente eliminato");
      loadAgentiOrphans(); onImported();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita"); }
  };
  const loadFierPartner = () => api.get("/import/fier-partner-sync-status").then((r) => {
    setFierPartner(r.data);
    if (!r.data.running && fierPartnerPoll.current) { clearInterval(fierPartnerPoll.current); fierPartnerPoll.current = null; loadGeo(); loadFierPartnerLog(); }
  }).catch(() => {});
  useEffect(() => { if (isAdmin) { loadFierPartner(); loadFierPartnerLog(); } return () => { if (fierPartnerPoll.current) clearInterval(fierPartnerPoll.current); }; }, [isAdmin]);
  const startFierPartner = async () => {
    if (!window.confirm("Allineare i Partner del CRM alla pagina Partner del gestionale Fier? Chi è in quella pagina diventa/resta Partner (prospect promossi); chi non c'è più viene declassato (specchio 100%).")) return;
    setFierPartnerBusy(true);
    try {
      await api.post("/import/fier-partner-sync-start");
      toast.success("Sincronizzazione partner avviata");
      await loadFierPartner();
      if (fierPartnerPoll.current) clearInterval(fierPartnerPoll.current);
      fierPartnerPoll.current = setInterval(loadFierPartner, 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la sincronizzazione"); }
    finally { setFierPartnerBusy(false); }
  };
  const restorePartnersExcel = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPartnerRestoreBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/import/partners-flag", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.message);
      loadFierPartner(); loadFierPartnerLog(); onImported();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Ripristino fallito"); }
    finally { setPartnerRestoreBusy(false); if (partnerRestoreRef.current) partnerRestoreRef.current.value = ""; }
  };
  const runRealign = async () => {
    if (!window.confirm("Riallineare la titolarità di TUTTI i contatti?\n\nRegole:\n• Forzatura manuale: sempre prioritaria\n• PROSPECT: territorio → direzionale (niente agente)\n• CLIENTI/PARTNER: agente (ultimo ordine) → territorio → direzionale")) return;
    setRealignBusy(true); setRealignMsg("");
    try {
      const r = await api.post("/admin/realign-ownership");
      setRealignMsg(r.data.message); toast.success(r.data.message); onImported();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Riallineamento fallito"); }
    finally { setRealignBusy(false); }
  };
  const loadFierFull = () => api.get("/import/fier-full-sync-status").then((r) => {
    setFierFull(r.data);
    if (!r.data.running && fierFullPoll.current) { clearInterval(fierFullPoll.current); fierFullPoll.current = null; loadGeo(); loadFierFullLog(); }
  }).catch(() => {});
  useEffect(() => { if (isAdmin) { loadFierFull(); loadFierFullLog(); } return () => { if (fierFullPoll.current) clearInterval(fierFullPoll.current); }; }, [isAdmin]);
  const startFierFull = async () => {
    if (!window.confirm("Avviare l'acquisizione delle anagrafiche TOTALI dal Gestionale Fier? Verranno arricchite le anagrafiche esistenti e CREATE quelle mancanti (Cliente se ha ordini, altrimenti Prospect), assegnate per territorio. È un'operazione lunga (migliaia di schede).")) return;
    setFierFullBusy(true);
    try {
      await api.post("/import/fier-full-sync-start");
      toast.success("Acquisizione anagrafiche totali avviata");
      await loadFierFull();
      if (fierFullPoll.current) clearInterval(fierFullPoll.current);
      fierFullPoll.current = setInterval(loadFierFull, 4000);
      if (fierDetailPoll.current) clearInterval(fierDetailPoll.current);
      fierDetailPoll.current = setInterval(loadFierDetail, 5000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare la sincronizzazione"); }
    finally { setFierFullBusy(false); }
  };

  const loadFierDetailLog = () => api.get("/import/fier-sync-log?kind=dettaglio").then((r) => setFierDetailLog(r.data)).catch(() => {});
  const loadFierDetail = () => api.get("/import/fier-detail-status").then((r) => {
    setFierDetail(r.data);
    if (!r.data.running && fierDetailPoll.current) { clearInterval(fierDetailPoll.current); fierDetailPoll.current = null; loadFierDetailLog(); }
  }).catch(() => {});
  useEffect(() => { if (isAdmin) { loadFierDetail(); loadFierDetailLog(); } return () => { if (fierDetailPoll.current) clearInterval(fierDetailPoll.current); }; }, [isAdmin]);
  const startFierDetail = async (force = false) => {
    if (!window.confirm(force
      ? "Riscaricare TUTTO (dati camerali, loghi e contratti) per OGNI anagrafica? È un'operazione lenta e pesante (riscarica anche tutti i PDF). Procedere?"
      : "Avviare l'arricchimento incrementale delle schede (solo anagrafiche non ancora arricchite)?")) return;
    setFierDetailBusy(true);
    try {
      await api.post(`/import/fier-detail-start?force=${force ? "true" : "false"}`);
      toast.success(force ? "Riscaricamento completo avviato" : "Arricchimento avviato");
      await loadFierDetail();
      if (fierDetailPoll.current) clearInterval(fierDetailPoll.current);
      fierDetailPoll.current = setInterval(loadFierDetail, 4000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impossibile avviare l'arricchimento"); }
    finally { setFierDetailBusy(false); }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="import-page">
        <h1 className="font-heading text-3xl font-black tracking-tight">Importa Dati</h1>
        <Card className="p-6 text-sm text-muted-foreground" data-testid="import-reserved">Sezione riservata all'amministratore.</Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="import-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Importa Dati</h1>
        <p className="text-sm text-muted-foreground">Sincronizza dati e anagrafiche dal Gestionale Fier e gestisci gli import avanzati. Sezione riservata all'amministratore.</p>
      </div>

      <div className="relative rounded-xl border-2 border-primary/70 p-4 pt-6 space-y-4" data-testid="auto-sync-group">
        <span className="absolute -top-2.5 left-4 rounded-full bg-background px-2 text-[11px] font-bold uppercase tracking-wide text-primary">Sincro automatiche</span>

      <div className="rounded-lg border border-primary/25 bg-primary/5 p-4" data-testid="auto-sync-recap">
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary"><Clock className="h-4 w-4" /> Riepilogo sincronizzazioni notturne</div>
        <p className="mb-2 text-xs text-muted-foreground">Tutti i dati vengono allineati automaticamente dal Gestionale Fier <b>ogni notte</b> (orari Europe/Rome). Il database e la Rubrica contatti riflettono il gestionale con al massimo un giorno di ritardo.</p>
        <ul className="space-y-1 text-xs text-foreground/90">
          <li className="flex items-center gap-2"><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">02:40</span> Agenti (Rete commerciale) — aggiunge solo le nuove agenzie con monitoraggio attivo</li>
          <li className="flex items-center gap-2"><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">03:00</span> Vendite / Ordini — ultimi 90 giorni (kWp e dettaglio prodotti)</li>
          <li className="flex items-center gap-2"><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">03:20</span> Attività (visite, telefonate, ecc.) — abbinate al cliente e impostate come «svolte»</li>
          <li className="flex items-center gap-2"><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">04:30</span> Anagrafiche TOTALI — arricchisce le esistenti e crea le mancanti (Cliente/Prospect)</li>
          <li className="flex items-center gap-2"><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">05:00</span> Ri-classifica Cliente/Prospect/Partner + ri-associa attività (backup incluso)</li>
          <li className="flex items-center gap-2"><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">05:15</span> Partner — allinea al 100% alla pagina Partner del gestionale (aggiunge e declassa)</li>
          <li className="flex items-center gap-2"><span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">05:45</span> Prospect direzionali — arricchisce gli indirizzi, aggiunge le email in Rubrica newsletter (categoria "Prospect (rubrica)") e archivia quelli senza dati</li>
        </ul>
        <p className="mt-2 text-[11px] text-muted-foreground">Puoi comunque lanciare ogni sincronizzazione manualmente dai pulsanti qui sotto.</p>
      </div>

      <Card className="border-primary/30 bg-primary/5 p-5" data-testid="fier-sync-card">
        <SyncToggleBar id="anagrafiche" enabled={isSyncEnabled("anagrafiche")} onToggle={toggleSync} title="Acquisisci e sincronizza anagrafiche dal Gestionale Fier" icon={Database} badge />
        {isSyncEnabled("anagrafiche") && (<>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Database className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="flex flex-wrap items-center gap-2"><span className="font-heading text-sm font-bold">Acquisisci e sincronizza anagrafiche dal Gestionale Fier</span><span className="rounded bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground" data-testid="badge-fier">Gestionale Fier</span></div>
              <div className="mt-0.5 text-xs text-muted-foreground">Legge le anagrafiche dal gestionale Fier e le abbina per <b>Partita IVA</b> (fallback: ragione sociale). Due modalità:</div>
              <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
                <li><b className="text-foreground">Sincronizza</b> — aggiorna/arricchisce le anagrafiche <b>già presenti</b> (indirizzo completo, P.IVA, agente, telefono, e-mail) senza sovrascrivere i dati esistenti, le ri-geolocalizza e <b>arricchisce le schede</b> con <b>dati camerali, data attivazione (firma contratto), logo e PDF del contratto</b> (incrementale).</li>
                <li><b className="text-foreground">Acquisisci tutte</b> — fa lo stesso e <b>crea anche le anagrafiche mancanti</b> (Cliente se ha ordini, altrimenti Prospect), assegnandole per territorio.</li>
              </ul>
              <div className="mt-1.5 text-[11px] text-muted-foreground">Usa <b>«Riscarica tutto (forza)»</b> per rigenerare integralmente camerali, loghi e contratti di ogni anagrafica.</div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-2">
            <Button onClick={startFierSync} disabled={fierBusy || fier?.running || fierFull?.running || fierDetail?.running} data-testid="fier-sync-btn">
              {(fierBusy || fier?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Sincronizza
            </Button>
            <Button onClick={startFierFull} disabled={fierFullBusy || fierFull?.running || fier?.running || fierDetail?.running} data-testid="fier-full-btn">
              {(fierFullBusy || fierFull?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Acquisisci tutte
            </Button>
            <Button variant="outline" size="sm" onClick={() => startFierDetail(true)} disabled={fierDetailBusy || fierDetail?.running || fierFull?.running || fier?.running} data-testid="fier-detail-force-btn">
              {(fierDetailBusy || fierDetail?.running) ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />} Riscarica tutto (forza)
            </Button>
          </div>
        </div>
        {fier && (fier.running || fier.finished) && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-semibold text-foreground">Sincronizza (esistenti)</span>
              {fier.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso</Badge>
                : fier.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
                : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
              <span className="text-muted-foreground">{fier.message}</span>
            </div>
            {fier.total > 0 && (
              <>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round((fier.processed / fier.total) * 100)}%` }} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground" data-testid="fier-sync-progress">
                  Esaminate {fier.processed} / {fier.total} · abbinate {fier.matched} · indirizzi aggiornati {fier.updated}
                </div>
              </>
            )}
          </div>
        )}
        {fierFull && (fierFull.running || fierFull.finished) && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-xs">
              {fierFull.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso</Badge>
                : fierFull.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
                : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
              <span className="text-muted-foreground">{fierFull.message}</span>
            </div>
            {fierFull.total > 0 && (
              <>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round((fierFull.processed / fierFull.total) * 100)}%` }} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground" data-testid="fier-full-progress">
                  Esaminate {fierFull.processed} / {fierFull.total} · aggiornate {fierFull.updated} · clienti nuovi {fierFull.created_clienti} · prospect nuovi {fierFull.created_prospect}
                </div>
              </>
            )}
          </div>
        )}
        {fierDetail && (fierDetail.running || fierDetail.finished) && (
          <div className="mt-4 rounded-lg border border-white/10 bg-black/20 p-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold text-foreground">Fase 2 · Arricchimento schede</span>
              {fierDetail.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso</Badge>
                : fierDetail.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
                : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
              <span className="text-muted-foreground">{fierDetail.message}</span>
            </div>
            {fierDetail.total > 0 && (
              <>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round((fierDetail.processed / fierDetail.total) * 100)}%` }} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground" data-testid="fier-detail-progress">
                  Schede: {fierDetail.processed} / {fierDetail.total} · arricchite {fierDetail.updated}
                </div>
              </>
            )}
            <SyncLogList rows={fierDetailLog} testid="fier-detail-log" />
          </div>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" />Sincronizzazione automatica ogni <b className="mx-1">notte</b> (anagrafiche complete + arricchimento schede: camerali, logo e contratto).</div>
        <SyncLogList rows={[...(fierFullLog || []), ...(fierLog || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))} testid="fier-full-log" showEmpty />
        </>)}
      </Card>

      <Card className="border-primary/30 bg-primary/5 p-5" data-testid="fier-partner-card">
        <SyncToggleBar id="partner" enabled={isSyncEnabled("partner")} onToggle={toggleSync} title="Acquisisci e sincronizza Partner dal Gestionale Fier" icon={Database} badge />
        {isSyncEnabled("partner") && (<>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Database className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="flex flex-wrap items-center gap-2"><span className="font-heading text-sm font-bold">Acquisisci e sincronizza Partner dal Gestionale Fier</span><span className="rounded bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground" data-testid="badge-fier">Gestionale Fier</span></div>
              <div className="mt-0.5 text-xs text-muted-foreground">Allinea i Partner del CRM <b>al 100%</b> alla pagina <b>Partner</b> del gestionale Fier: flagga i clienti corrispondenti, <b>promuove</b> i prospect e <b>declassa</b> chi non è più in quella pagina. Nessun'altra verifica. In alternativa puoi allineare da file con <b>«Ripristina da Excel»</b>.</div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-2">
            <Button onClick={startFierPartner} disabled={fierPartnerBusy || fierPartner?.running} data-testid="fier-partner-btn">
              {(fierPartnerBusy || fierPartner?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Sincronizza partner
            </Button>
            <input ref={partnerRestoreRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={restorePartnersExcel} data-testid="partner-restore-input" />
            <Button variant="outline" onClick={() => partnerRestoreRef.current?.click()} disabled={partnerRestoreBusy} data-testid="partner-restore-btn">
              {partnerRestoreBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Ripristina da Excel
            </Button>
          </div>
        </div>
        <SyncSemaphore rows={fierPartnerLog} running={fierPartner?.running} />
        {fierPartner && (fierPartner.running || fierPartner.finished) && (
          <div className="mt-4 flex items-center gap-2 text-xs">
            {fierPartner.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso</Badge>
              : fierPartner.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
              : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
            <span className="text-muted-foreground" data-testid="fier-partner-msg">{fierPartner.message}</span>
          </div>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" />Sincronizzazione automatica ogni <b className="mx-1">notte</b> (elenco partner del gestionale).</div>
        <SyncLogList rows={fierPartnerLog} testid="fier-partner-log" showEmpty />
        </>)}
      </Card>

      <Card className="border-primary/30 bg-primary/5 p-5" data-testid="realign-card">
        <SyncToggleBar id="realign" enabled={isSyncEnabled("realign")} onToggle={toggleSync} title="Riallinea titolarità contatti" icon={RefreshCw} />
        {isSyncEnabled("realign") && (<>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="font-heading text-sm font-bold">Riallinea titolarità contatti</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Ricalcola l'agente titolare di <b>tutti</b> i contatti. La <b>forzatura manuale è sempre prioritaria</b>. I <b>prospect</b> seguono solo <b>territorio → direzionale</b> (niente agente). I <b>clienti/partner</b> seguono <b>agente (ultimo ordine) → territorio → direzionale</b>. Utile subito dopo un import/sync, senza aspettare il ricalcolo notturno.</div>
            </div>
          </div>
          <Button onClick={runRealign} disabled={realignBusy} className="shrink-0" data-testid="realign-btn">
            {realignBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Riallinea ora
          </Button>
        </div>
        {realignMsg && <div className="mt-3 flex items-center gap-2 text-xs"><Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />fatto</Badge><span className="text-muted-foreground" data-testid="realign-msg">{realignMsg}</span></div>}
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" />Riallineamento automatico ogni <b className="mx-1">notte</b> (riclassifica 05:00) e dopo la sincro anagrafiche.</div>
        </>)}
      </Card>

      <Card className="border-primary/30 bg-primary/5 p-5" data-testid="fier-agenti-card">
        <SyncToggleBar id="agenti" enabled={isSyncEnabled("agenti")} onToggle={toggleSync} title="Acquisisci e sincronizza Agenti (Rete commerciale) dal Gestionale Fier" icon={Building2} badge />
        {isSyncEnabled("agenti") && (<>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="flex flex-wrap items-center gap-2"><span className="font-heading text-sm font-bold">Acquisisci e sincronizza Agenti (Rete commerciale) dal Gestionale Fier</span><span className="rounded bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground" data-testid="badge-fier">Gestionale Fier</span></div>
              <div className="mt-0.5 text-xs text-muted-foreground">Legge i <b>Riferimenti interni</b> del gestionale Fier e <b>aggiunge solo le nuove agenzie/agenti con «monitoraggio attivo»</b>. Gli agenti nel CRM <b>non più presenti/attivi</b> nel gestionale vengono <b>segnalati qui sotto</b> per l'eventuale eliminazione manuale (i loro contatti passano a territorio, poi direzionale).</div>
            </div>
          </div>
          <Button onClick={startFierAgenti} disabled={fierAgentiBusy || fierAgenti?.running} className="shrink-0" data-testid="fier-agenti-btn">
            {(fierAgentiBusy || fierAgenti?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Importa agenti
          </Button>
        </div>
        {fierAgenti && (fierAgenti.running || fierAgenti.finished) && (
          <div className="mt-4 flex items-center gap-2 text-xs">
            {fierAgenti.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso</Badge>
              : fierAgenti.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
              : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
            <span className="text-muted-foreground" data-testid="fier-agenti-msg">{fierAgenti.message}</span>
          </div>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" />Verifica automatica ogni <b className="mx-1">notte</b> (solo aggiunta nuove agenzie attive).</div>
        {agentiOrphans.length > 0 && (
          <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3" data-testid="agenti-orphans">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-500"><AlertTriangle className="h-3.5 w-3.5" />{agentiOrphans.length} agenti nel CRM non più presenti/attivi nel gestionale</div>
            <div className="mt-1 text-[11px] text-muted-foreground">Eliminandone uno, i suoi contatti passano prima per <b>territorio</b>, poi a <b>direzionale</b>.</div>
            <div className="mt-2 space-y-1.5">
              {agentiOrphans.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-3 rounded border border-border bg-background px-2.5 py-1.5 text-xs" data-testid={`orphan-${o.id}`}>
                  <span><b>{o.name}</b> <span className="text-muted-foreground">· {o.contacts} contatti</span></span>
                  <Button size="sm" variant="destructive" className="h-7" onClick={() => deleteOrphanAgent(o)} data-testid={`orphan-del-${o.id}`}>Elimina</Button>
                </div>
              ))}
            </div>
          </div>
        )}
        <SyncLogList rows={fierAgentiLog} testid="fier-agenti-log" showEmpty />
        </>)}
      </Card>

      <Card className="border-primary/30 bg-primary/5 p-5" data-testid="fier-attivita-card">
        <SyncToggleBar id="attivita" enabled={isSyncEnabled("attivita")} onToggle={toggleSync} title="Acquisisci e sincronizza Attività dal Gestionale Fier" icon={Activity} badge />
        {isSyncEnabled("attivita") && (<>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Activity className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="flex flex-wrap items-center gap-2"><span className="font-heading text-sm font-bold">Acquisisci e sincronizza Attività dal Gestionale Fier</span><span className="rounded bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground" data-testid="badge-fier">Gestionale Fier</span></div>
              <div className="mt-0.5 text-xs text-muted-foreground">Importa le <b>attività</b> (visite, telefonate, ecc.) registrate nel gestionale Fier, le <b>abbina al cliente</b> per ragione sociale, le imposta come <b>«svolte»</b> e le collega all'agenzia titolare. I duplicati vengono evitati: le attività già presenti vengono <b>allineate</b> (nessun raddoppio).</div>
            </div>
          </div>
          <Button onClick={startFierActivities} disabled={fierActBusy || fierAct?.running} className="shrink-0" data-testid="fier-attivita-btn">
            {(fierActBusy || fierAct?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Sincronizza ora
          </Button>
        </div>
        {fierAct && (fierAct.running || fierAct.finished) && (
          <div className="mt-4">
            <div className="flex items-center gap-2 text-xs">
              {fierAct.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso</Badge>
                : fierAct.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
                : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
              <span className="text-muted-foreground" data-testid="fier-attivita-msg">{fierAct.message}</span>
            </div>
            {fierAct.total > 0 && (
              <>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.round((fierAct.processed / fierAct.total) * 100)}%` }} />
                </div>
                <div className="mt-1 text-xs text-muted-foreground" data-testid="fier-attivita-progress">
                  Esaminate {fierAct.processed} / {fierAct.total} · nuove {fierAct.created} · allineate {fierAct.adopted} · senza cliente {fierAct.unmatched}
                </div>
              </>
            )}
          </div>
        )}
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" />Sincronizzazione automatica ogni <b className="mx-1">notte alle 03:20</b> (Europe/Rome).</div>
        <SyncLogList rows={fierActLog} testid="fier-attivita-log" showEmpty />
        </>)}
      </Card>

      <Card className="border-primary/30 bg-primary/5 p-5" data-testid="fier-vendite-card">
        <SyncToggleBar id="vendite" enabled={isSyncEnabled("vendite")} onToggle={toggleSync} title="Acquisisci e sincronizza Vendite e preventivi dal Gestionale Fier" icon={Receipt} badge />
        {isSyncEnabled("vendite") && (<>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Receipt className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <div className="flex flex-wrap items-center gap-2"><span className="font-heading text-sm font-bold">Acquisisci e sincronizza Vendite e preventivi dal Gestionale Fier</span><span className="rounded bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground" data-testid="badge-fier">Gestionale Fier</span></div>
              <div className="mt-0.5 text-xs text-muted-foreground">Importa dal gestionale Fier gli <b>ordini</b> (Conferme d'Ordine) e i <b>preventivi</b> con <b>comandi separati</b>, in modo completo: <b>importo</b>, <b>kWp</b>, <b>dettaglio prodotti</b> (moduli, articoli e servizi), <b>numero documento</b>, <b>modalità di pagamento</b>, <b>indice di contribuzione</b> e <b>PDF</b>. Crea e promuove automaticamente i clienti collegati. Sostituisce il caricamento Excel delle Vendite. <b>Se una corsa "Sincronizza tutto" si interrompe, rilanciala: riprende automaticamente dai documenti mancanti.</b> <span className="text-emerald-500">Al termine della sincronizzazione <b>Ordini</b> viene aggiornata automaticamente anche la sincronizzazione delle <b>commesse aperte (id_t=42)</b> da cui si calcola l'<b>esposizione</b> dei clienti negli Affidamenti.</span></div>
            </div>
          </div>
          <div className="flex shrink-0 flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">Ordini</span>
              <Button onClick={() => startFierVendite(false)} disabled={fierVBusy || fierV?.running} data-testid="fier-vendite-btn-90">
                {(fierVBusy || fierV?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Ultimi 90 giorni
              </Button>
              <Button variant="outline" onClick={() => startFierVendite(true)} disabled={fierVBusy || fierV?.running} className="border-primary/40 text-primary hover:bg-primary/10" data-testid="fier-vendite-btn-full">
                {(fierVBusy || fierV?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />} Sincronizza tutto
              </Button>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-sky-500">Preventivi</span>
              <Button onClick={() => startFierPreventivi(false)} disabled={fierQBusy || fierQ?.running} data-testid="fier-preventivi-btn-90">
                {(fierQBusy || fierQ?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Ultimi 90 giorni
              </Button>
              <Button variant="outline" onClick={() => startFierPreventivi(true)} disabled={fierQBusy || fierQ?.running} className="border-sky-500/40 text-sky-500 hover:bg-sky-500/10" data-testid="fier-preventivi-btn-full">
                {(fierQBusy || fierQ?.running) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Database className="mr-2 h-4 w-4" />} Sincronizza tutto
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="intraday-sync-block">
          <Clock className="h-4 w-4 shrink-0 text-emerald-500" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Aggiornamento del giorno (ordini + preventivi)</div>
            <div className="text-[11px] text-muted-foreground">
              Automatico ogni giorno alle <b>18:30</b>. Ultimo aggiornamento: <b>{intraday?.finished_at ? new Date(intraday.finished_at).toLocaleString("it-IT") : "—"}</b>
              {intraday?.failed?.length ? <span className="text-amber-500"> · {intraday.failed.length} passi falliti</span> : ""}
            </div>
          </div>
          <Button size="sm" onClick={runIntraday} disabled={intradayBusy} className="border-emerald-500/40 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="intraday-sync-btn">
            {intradayBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Aggiorna ora ordini e preventivi di oggi
          </Button>
        </div>
        <div className="mt-3 rounded-md border border-border/60 bg-muted/30 p-3" data-testid="fier-cutoff">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Clock className="h-3.5 w-3.5 text-primary" /> Cut iniziale — considera i dati dal mese</div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">Ordini da</span>
              <input type="month" value={cutoffOrders} onChange={(e) => setCutoffOrders(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm" data-testid="cutoff-orders-input" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-sky-500">Preventivi da</span>
              <input type="month" value={cutoffQuotes} onChange={(e) => setCutoffQuotes(e.target.value)} className="rounded-md border border-border bg-background px-2 py-1 text-sm" data-testid="cutoff-quotes-input" />
            </label>
            <Button size="sm" variant="outline" onClick={saveCutoff} disabled={cutoffBusy} data-testid="cutoff-save-btn">
              {cutoffBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Applica
            </Button>
          </div>
          <div className="mt-1.5 text-[11px] text-muted-foreground">Lascia vuoto = nessun taglio (tutti i dati). Impostando un mese, i documenti precedenti vengono <b>esclusi dal CRM</b> (rimossi ora e non più importati).</div>
        </div>
        {((fierV && (fierV.running || fierV.finished)) || (fierQ && (fierQ.running || fierQ.finished))) && (
          <div className="mt-4 space-y-3">
            {fierV && (fierV.running || fierV.finished) && (
              <div className="rounded-md border border-emerald-500/30 p-2" data-testid="fier-vendite-block">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">Ordini</span>
                  {fierV.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso ({fierV.mode})</Badge>
                    : fierV.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
                    : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
                  <span className="text-muted-foreground">{fierV.message}</span>
                </div>
                {fierV.total > 0 && (
                  <>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.round((fierV.processed / fierV.total) * 100)}%` }} />
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground" data-testid="fier-vendite-progress">
                      Ordini {fierV.processed} / {fierV.total} · salvati {fierV.orders_upserted} · clienti nuovi {fierV.clienti_nuovi} · promossi {fierV.clienti_promossi}
                    </div>
                  </>
                )}
              </div>
            )}
            {fierQ && (fierQ.running || fierQ.finished) && (
              <div className="rounded-md border border-sky-500/30 p-2" data-testid="fier-preventivi-block">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-sky-500">Preventivi</span>
                  {fierQ.running ? <Badge className="bg-blue-600 text-white"><Loader2 className="mr-1 h-3 w-3 animate-spin" />in corso ({fierQ.mode})</Badge>
                    : fierQ.error ? <Badge className="bg-destructive text-white"><AlertTriangle className="mr-1 h-3 w-3" />errore</Badge>
                    : <Badge className="bg-emerald-600 text-white"><CheckCircle2 className="mr-1 h-3 w-3" />completata</Badge>}
                  <span className="text-muted-foreground">{fierQ.message}</span>
                </div>
                {fierQ.total > 0 && (
                  <>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-sky-500 transition-all" style={{ width: `${Math.round((fierQ.processed / fierQ.total) * 100)}%` }} />
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground" data-testid="fier-preventivi-progress">
                      Preventivi {fierQ.processed} / {fierQ.total} · salvati {fierQ.quotes_upserted}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <div className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground"><Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /><span>Sincronizzazione automatica ogni <b>notte alle 03:00</b> (ultimi 90 giorni) e <b>completa ogni domenica notte</b>.</span></div>
        <SyncLogList rows={fierVLog} testid="fier-vend-log" title="Storico ordini" />
        <SyncLogList rows={fierQLog} testid="fier-prev-log" title="Storico preventivi" showEmpty />
        </>)}
      </Card>
      </div>

      <div className="relative rounded-xl border-2 border-primary/70 p-4 pt-6 space-y-4" data-testid="assoc-enrich-group">
        <span className="absolute -top-2.5 left-4 rounded-full bg-background px-2 text-[11px] font-bold uppercase tracking-wide text-primary">Associazioni ed arricchimenti</span>
        <EnrichCard enabled={isSyncEnabled("enrich")} onToggle={toggleSync} />
        <ReclassifyCard enabled={isSyncEnabled("reclassify")} onToggle={toggleSync} />
        <ReassociateCard enabled={isSyncEnabled("reassociate")} onToggle={toggleSync} />
      </div>

      <div className="relative rounded-xl border-2 border-primary/70 p-4 pt-6 space-y-4" data-testid="extra-import-group">
        <span className="absolute -top-2.5 left-4 rounded-full bg-background px-2 text-[11px] font-bold uppercase tracking-wide text-primary">Extra importazioni</span>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {others.filter((k) => k.kind !== "attivita" && k.kind !== "anagrafiche").map((k) => <ImportCard key={k.kind} {...k} onDone={onImported} agencies={k.kind === "prospects" ? agencies : []} />)}
        </div>

        <ProspectAssignmentPanel />

      {history.length > 0 && (
        <Card className="p-5" data-testid="import-history">
          <h3 className="mb-3 font-heading text-sm font-bold">Storico import</h3>
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted"><tr>
                <th className="px-2 py-1.5 text-left font-semibold">Data</th>
                <th className="px-2 py-1.5 text-left font-semibold">Tipo</th>
                <th className="px-2 py-1.5 text-left font-semibold">File</th>
                <th className="px-2 py-1.5 text-right font-semibold">Righe</th>
                <th className="px-2 py-1.5 text-right font-semibold">Nuovi</th>
                <th className="px-2 py-1.5 text-right font-semibold">Aggiornati</th>
                <th className="px-2 py-1.5 text-left font-semibold">Campi arricchiti</th>
                <th className="px-2 py-1.5 text-left font-semibold">Utente</th>
              </tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-border" data-testid={`history-row-${h.id}`}>
                    <td className="whitespace-nowrap px-2 py-1 text-muted-foreground">{h.created_at ? new Date(h.created_at).toLocaleString("it-IT") : "—"}</td>
                    <td className="px-2 py-1 font-semibold capitalize">{h.kind}</td>
                    <td className="max-w-[200px] truncate px-2 py-1 text-muted-foreground">{h.filename || "—"}</td>
                    <td className="px-2 py-1 text-right font-mono">{h.total}</td>
                    <td className="px-2 py-1 text-right font-mono text-emerald-600">{h.inserted ?? h.created_clienti ?? "—"}</td>
                    <td className="px-2 py-1 text-right font-mono text-blue-600">{h.updated ?? "—"}</td>
                    <td className="max-w-[240px] px-2 py-1 text-muted-foreground">{h.fields && Object.keys(h.fields).length ? Object.entries(h.fields).map(([k, v]) => `${k} ${v}`).join(" · ") : "—"}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.user || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      </div>
    </div>
  );
}
